import React, { useState, useEffect } from "react";
import { ChevronDown, Globe, ArrowRight, Eye } from "lucide-react";

const HeroSection = ({ scrollToSection }) => {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900">
        {/* Layered gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-emerald-900/30"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/40 via-transparent to-yellow-900/20"></div>

        {/* Organic floating shapes */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-emerald-400/10 to-blue-400/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-yellow-400/12 to-emerald-400/10 rounded-full blur-2xl animate-bounce delay-1000"></div>
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-gradient-to-br from-blue-400/15 to-yellow-400/12 rounded-full blur-3xl animate-pulse delay-500"></div>
        <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-gradient-to-br from-yellow-300/8 to-blue-300/12 rounded-full blur-2xl animate-bounce delay-700"></div>

        {/* Subtle dot pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)`,
            backgroundSize: "32px 32px",
          }}
        ></div>

        {/* Elegant geometric shapes */}
        <div className="absolute top-40 right-40">
          <div className="w-4 h-4 bg-emerald-400/40 rounded-full animate-pulse delay-300"></div>
        </div>
        <div className="absolute bottom-60 left-60">
          <div className="w-6 h-6 bg-yellow-400/30 rounded-full animate-bounce delay-500"></div>
        </div>
        <div className="absolute top-60 left-1/3">
          <div className="w-3 h-3 bg-blue-400/50 rounded-full animate-pulse delay-700"></div>
        </div>

        {/* Subtle wave pattern */}
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20">
          <svg
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            <path
              d="M0,40 C300,80 600,20 900,60 C1000,80 1100,40 1200,60 L1200,120 L0,120 Z"
              fill="url(#waveGradient)"
            />
            <defs>
              <linearGradient
                id="waveGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#eab308" stopOpacity="0.3" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-7xl mx-auto">
        <div className="mb-12 transform hover:scale-105 transition-transform duration-500">
          <div className="inline-block p-6 bg-gradient-to-br from-emerald-500/20 via-blue-500/20 to-yellow-500/20 rounded-full mb-8 backdrop-blur-sm border border-white/20 shadow-2xl">
            <Globe className="w-20 h-20 text-white drop-shadow-lg" />
          </div>
        </div>

        <h1 className="text-6xl md:text-8xl font-black text-white mb-8 leading-tight drop-shadow-2xl">
          Building Africa's
          <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-blue-400 to-yellow-400 animate-pulse">
            Future
          </span>
        </h1>

        <p className="text-xl md:text-3xl text-slate-200 mb-12 max-w-4xl mx-auto leading-relaxed font-light drop-shadow-lg">
          <span className="text-yellow-400 font-semibold">Tradition.</span>{" "}
          <span className="text-emerald-400 font-semibold">Innovation.</span>{" "}
          <span className="text-blue-400 font-semibold">Growth.</span>
          <br />
          <span className="text-lg md:text-xl mt-4 block">
            A dynamic holding company uniting excellence across Africa and
            beyond.
          </span>
        </p>

        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
          <button
            onClick={() => scrollToSection("subsidiaries")}
            className="group bg-gradient-to-r from-emerald-500 via-blue-500 to-yellow-500 hover:from-yellow-500 hover:via-emerald-500 hover:to-blue-500 text-white px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-110 shadow-2xl hover:shadow-emerald-500/25 flex items-center space-x-3 border border-white/20"
          >
            <span>Explore Our Subsidiaries</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
          </button>

          <button
            onClick={() => scrollToSection("about")}
            className="group bg-transparent border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-slate-900 px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-110 shadow-2xl hover:shadow-emerald-500/25 flex items-center space-x-3 backdrop-blur-sm"
          >
            <span>Learn More</span>
            <Eye className="w-6 h-6 group-hover:rotate-12 transition-transform duration-300" />
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center space-y-2">
          <span className="text-white/60 text-sm font-medium">
            Scroll to explore
          </span>
          <ChevronDown className="w-8 h-8 text-emerald-400" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
